<template>
  <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
    <bulk-selector
      v-if="shouldShowMultirowSelect"
      :grid-api="props.gridApi"
    />

    <bulk-actions
      v-if="shouldShowMultirowSelect"
      :grid-api="props.gridApi"
      :settings="props.config.multirowSelectSettings"
    />

    <quick-filter
      v-if="props.config.enableQuickFilter"
      :grid-api="props.gridApi"
    />

    <filter-presets
      v-if="props.config.enablePresets"
      ref="filterPresetsRef"
      :admin="props.admin"
      :grid-api="props.gridApi"
      :grid-changed-from-preset="gridChangedFromPreset"
      :page-load-preset-id="pageLoadPresetId"
      :preset-id-param-key="props.config.presetsSettings?.presetIdParamKey || 'viewId'"
      :roles="props.config.presetsSettings?.roles || []"
      :site-scopes="props.config.presetsSettings?.availableSiteScopes || []"
      :scope="props.config.presetsSettings?.scope"
      @set-preset="handleSetPreset"
      @clear-preset="handleClearPreset"
    />

    <export-button
      v-if="props.config.enableExportButton"
      :admin="props.admin"
      :api-settings="props.config.apiSettings"
      :config="props.config"
      :default-filename="props.config.exportButtonSettings.defaultFilename || ''"
      :grid-api="props.gridApi"
      :header="props.config.exportButtonSettings?.header || 'Export Data'"
      :multi-select-enabled="shouldShowMultirowSelect"
      :queue-name="props.config.exportButtonSettings?.queueName || props.defaultQueueName"
      :path="props.config.exportButtonSettings?.endpoint"
    />
  </div>
</template>

<script setup>
import { computed, inject, provide, ref } from 'vue'
import { injectRowModelType } from '@gu-datagrid-new/composables/RowModelType'

import { isCondition, parseCondition } from '@shared/utils/json-logic'

import BulkActions from '@gu-datagrid-new/components/grid-menu/BulkActions.vue'
import BulkSelector from '@gu-datagrid-new/components/grid-menu/BulkSelector.vue'
import ExportButton from '@gu-datagrid-new/components/grid-menu/ExportButton.vue'
import FilterPresets from '@gu-datagrid-new/components/grid-menu/FilterPresets.vue'
import QuickFilter from '@gu-datagrid-new/components/grid-menu/QuickFilter.vue'

const props = defineProps({
  admin: {
    type: Boolean,
    default: false,
    description: 'Whether the user is an administrator',
  },
  config: {
    type: Object,
    default: () => ({
      enableExportButton: false,
      enableMultirowSelect: false,
      enablePresets: false,
      enableQuickFilter: true,
      exportButtonSettings: {},
      multirowSelectSettings: {},
      presetsSettings: {},
    }),
    description: 'Datagrid configuration (both GU props and Ag-Grid options)',
  },
  gridApi: {
    type: Object,
    default: null,
  },
  initialColumnState: {
    type: Array,
    default: () => [],
    description: 'Initial column state to reset to when clearing a preset',
  },
  pageViewId: {
    type: Number,
    default: null,
    description: 'Page view id',
  },
  userRole: {
    type: String,
    default: 'Role1',
    description: 'Current user role for preset preloading',
  },
  defaultQueueName: {
    type: String,
    default: 'NOT_SET',
    description: 'Default queueName to use for export button',
  },
})

const rowModelType = injectRowModelType()
const gridState = inject('gridState')
const { getCurrentGridState, setFilterModel } = gridState

const filterPresetsRef = ref(null)

const selectedPresetState = ref(null)

const isPresetAction = ref(false)
provide('presetActionState', isPresetAction)

const pageVarsQuery = inject('pageVarsQuery')
const user = computed(() => pageVarsQuery.pageVars?.value?.user || window.siteVars?.user || {})

const shouldShowMultirowSelect = computed(() => {
  if (props.config.enableMultirowSelect) {
    return isCondition(props.config.enableMultirowSelect)
      ? parseCondition(props.config.enableMultirowSelect, { user: user.value })
      : props.config.enableMultirowSelect
  }

  return false
})

const handleSetPreset = (presetState) => {
  isPresetAction.value = true
  selectedPresetState.value = presetState
  if (!props.gridApi || !presetState) {
    isPresetAction.value = false
    return
  }
  setFilterModel(props.gridApi, presetState.agGrid.filterModel)
  props.gridApi.applyColumnState({ state: presetState.agGrid.columnState, applyOrder: true })
  if (rowModelType.value === 'serverSide') {
    props.gridApi.refreshServerSide({ purge: false })
  }
  isPresetAction.value = false
}

const handleClearPreset = () => {
  isPresetAction.value = true
  selectedPresetState.value = null
  if (props.gridApi) {
    setFilterModel(props.gridApi, {})

    if (props.initialColumnState && props.initialColumnState.length > 0) {
      props.gridApi.applyColumnState({ state: props.initialColumnState, applyOrder: true })
    }
    if (rowModelType.value === 'serverSide') {
      props.gridApi.refreshServerSide({ purge: false })
    }
  }
  isPresetAction.value = false
}

const pageLoadPresetId = computed(() => {
  if (props.pageViewId) return props.pageViewId

  const preloadPresetByRole = presetsSettings.value?.preloadPresetByRole || {}
  return preloadPresetByRole[props.userRole] || null
})

const gridChangedFromPreset = computed(() => {
  if (!props.gridApi || !selectedPresetState.value) return false
  const presetState = selectedPresetState.value
  // Use reactive refs so this computed re-runs when filter/quickFilter change (fixes ILCM-9451)
  const currentFilterModel = gridState?.filterModel?.value ?? getCurrentGridState(props.gridApi).agGrid?.filterModel
  const currentQuickFilter = gridState?.quickFilterValue?.value ?? getCurrentGridState(props.gridApi).quickFilterValue
  const currentColumnState = getCurrentGridState(props.gridApi).agGrid?.columnState

  const isFilterModelEqual = JSON.stringify(currentFilterModel) === JSON.stringify(presetState.agGrid?.filterModel)
  const isColumnStateEqual = JSON.stringify(currentColumnState) === JSON.stringify(presetState.agGrid?.columnState)
  const isQuickFilterEqual = currentQuickFilter === presetState.quickFilterValue

  return !(isFilterModelEqual && isColumnStateEqual && isQuickFilterEqual)
})

const presetsSettings = computed(() => {
  const settings = props.config.presetsSettings.value || {}

  return settings
})
</script>
